import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { onAuthChange } from "../services/auth.service";
import { getUserProfile, saveUserAddress } from "../services/user.service";

const Checkout = () => {
  const { cart, totalTax, cartSubtotal, totalShipping, cartTotal, taxCalculation } = useCart();
  const navigate = useNavigate();
  const hasExclusiveTax = taxCalculation?.exclusive > 0;
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    postCode: "",
  });



  useEffect(() => {
    const unsubscribe = onAuthChange(async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const profile = await getUserProfile(currentUser.uid);
        if (profile) {
          setSavedAddresses(profile.addresses || []);
          setFormData(prev => ({
            ...prev,
            name: profile.name || "",
            email: currentUser.email || profile.email || "",
            phone: profile.phone || ""
          }));
        }
      }
    });
    return () => unsubscribe();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Length constraints
    if (name === 'phone' && value.length > 10) return;
    if (name === 'postCode' && value.length > 6) return;
    
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectAddress = (addr) => {
    setFormData({
      ...formData,
      name: addr.fullName || "",
      phone: addr.phone || "",
      address: addr.addressLine1 || "",
      postCode: addr.zipCode || "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (cart.length === 0) return;

    const newErrors = {};
    if (formData.name.trim().length < 2) newErrors.name = "Full name must be at least 2 characters";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Please enter a valid email address";
    if (!/^\d{10}$/.test(formData.phone)) newErrors.phone = "Phone number must be exactly 10 digits";
    if (formData.address.trim().length < 10) newErrors.address = "Please provide a complete address (min 10 chars)";
    if (!/^\d{6}$/.test(formData.postCode)) newErrors.postCode = "Pincode must be exactly 6 digits";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      window.scrollTo({ top: 200, behavior: 'smooth' });
      return;
    }

    setErrors({});
    setLoading(true);
    try {
      // Auto-save address if logged in
      if (user) {
        const addressToSave = {
          fullName: formData.name,
          phone: formData.phone,
          addressLine1: formData.address,
          zipCode: formData.postCode
        };
        await saveUserAddress(user.uid, addressToSave);
      }

      const orderData = {
        userId: user?.uid || "guest",
        customerInfo: {
          ...formData,
        },
        items: cart,
        subtotal: cartSubtotal,
        tax: totalTax,
        shipping: totalShipping,
        total: cartTotal,
      };

      navigate("/payment", { state: { orderData } });
    } catch (err) {
      console.error("Checkout error:", err);
      alert("Failed to proceed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <section className='checkout py-80'>
        <div className='container text-center'>
          <h5>Your cart is empty.</h5>
          <Link to='/shop' className='btn btn-main-600 mt-16 rounded-12'>
            Back to Shop
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className='checkout py-80'>
      <div className='container container-lg'>
        <div className='row'>
          <div className='col-xl-9 col-lg-8'>
            <form onSubmit={handleSubmit} className='pe-xl-5'>
              {savedAddresses.length > 0 && (
                <div className="mb-40">
                  <h6 className="mb-24 fw-bold">Select Saved Address</h6>
                  <div className="row gy-3">
                    {savedAddresses.map((addr) => (
                      <div key={addr.id} className="col-md-6">
                        <div
                          onClick={() => handleSelectAddress(addr)}
                          className="border border-gray-100 p-16 rounded-12 hover-border-main-600 transition-1 cursor-pointer bg-gray-50 shadow-sm h-100"
                        >
                          <div className="flex-between mb-8">
                            <span className="fw-bold text-sm">{addr.fullName}</span>
                            <span className="text-xs text-main-600 fw-bold">Select</span>
                          </div>
                          <p className="text-xs text-gray-500 mb-0 line-clamp-2">{addr.addressLine1}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <h6 className='text-xl mb-24 fw-bold'>Billing Details</h6>
              <div className='row gy-4'>
                <div className='col'>
                  <label className="text-gray-600 text-sm mb-8 fw-medium">Full Name</label>
                  <input
                    type='text'
                    name='name'
                    className={`common-input border-gray-100 rounded-12 ${errors.name ? 'border-danger-600' : ''}`}
                    placeholder='Enter Full Name'
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                  {errors.name && <span className="text-danger-600 text-xs mt-4 d-block">{errors.name}</span>}
                </div>

                <div className='col-12'>
                  <label className="text-gray-600 text-sm mb-8 fw-medium">Email Address</label>
                  <input
                    type='email'
                    name='email'
                    className={`common-input border-gray-100 rounded-12 ${errors.email ? 'border-danger-600' : ''}`}
                    placeholder='Enter Email Address'
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                  {errors.email && <span className="text-danger-600 text-xs mt-4 d-block">{errors.email}</span>}
                </div>

                <div className='col-12'>
                  <label className="text-gray-600 text-sm mb-8 fw-medium">Phone Number</label>
                  <input
                    type='tel'
                    name='phone'
                    className={`common-input border-gray-100 rounded-12 ${errors.phone ? 'border-danger-600' : ''}`}
                    placeholder='Enter phone number'
                    required
                    maxLength="10"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                  {errors.phone && <span className="text-danger-600 text-xs mt-4 d-block">{errors.phone}</span>}
                </div>

                <div className='col-12'>
                  <label className="text-gray-600 text-sm mb-8 fw-medium">Shipping Address</label>
                  <input
                    type='text'
                    name='address'
                    className={`common-input border-gray-100 rounded-12 ${errors.address ? 'border-danger-600' : ''}`}
                    placeholder='Enter Full Address'
                    required
                    value={formData.address}
                    onChange={handleInputChange}
                  />
                  {errors.address && <span className="text-danger-600 text-xs mt-4 d-block">{errors.address}</span>}
                </div>

                <div className='col-sm-12'>
                  <label className="text-gray-600 text-sm mb-8 fw-medium">Post Code</label>
                    <input
                      type='text'
                      name='postCode'
                      className={`common-input border-gray-100 rounded-12 ${errors.postCode ? 'border-danger-600' : ''}`}
                      placeholder='Post Code'
                      required
                      maxLength="6"
                      value={formData.postCode}
                      onChange={handleInputChange}
                    />
                  {errors.postCode && <span className="text-danger-600 text-xs mt-4 d-block">{errors.postCode}</span>}
                </div>

              </div>

              <button
                type='submit'
                className='btn bg-main-600 text-white mt-10 py-18 w-100 rounded-12 fw-bold hover-bg-main-700 shadow-sm transition-2 flex-center gap-12'
                disabled={loading}
              >
                {loading ? "Processing..." : "Proceed to Payment"} <i className="ph ph-arrow-right"></i>
              </button>
            </form>
          </div>
          <div className='col-xl-3 col-lg-4'>
            <div className='checkout-sidebar'>
              <h6 className='mb-24 fw-bold'>Your Order Details</h6>
              <div className='border border-gray-100 rounded-16 px-24 py-32 bg-white shadow-sm'>
                <div className='mb-24 pb-16 border-bottom border-gray-100 flex-between gap-8'>
                  <span className='text-gray-900 fw-bold text-lg'>Product</span>
                  <span className='text-gray-900 fw-bold text-lg'>Subtotal</span>
                </div>
                <div className="order-items-scroll scroll-sm mb-24" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {cart.map((item) => (
                    <div
                      key={`${item.id}-${item.variantId}`}
                      className='flex-between gap-16 mb-20 pe-8'
                    >
                      <div className='flex-align gap-12'>
                        <span className='text-gray-700 fw-medium text-sm text-line-1' style={{ maxWidth: '120px' }}>
                          {item.name}
                        </span>
                        <span className='text-gray-400 text-xs'>x{item.quantity}</span>
                      </div>
                      <span className='text-gray-900 fw-bold text-sm'>
                        Rs.{(item.price * item.quantity).toFixed(0)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className='border-top border-gray-100 pt-24 mt-24'>
                  <div className='mb-16 flex-between gap-8'>
                    <span className='text-gray-600 fw-medium text-sm'>Subtotal</span>
                    <span className='text-gray-900 fw-bold text-sm'>Rs.{cartSubtotal.toFixed(0)}</span>
                  </div>
                  {hasExclusiveTax && (
                    <div className='mb-16 flex-between gap-8'>
                      <span className='text-gray-600 fw-medium text-sm'>GST</span>
                      <span className='text-gray-900 fw-bold text-sm'>Rs.{taxCalculation.exclusive.toFixed(0)}</span>
                    </div>
                  )}
                  <div className='mb-16 flex-between gap-8'>
                    <span className='text-gray-600 fw-medium text-sm'>Shipping</span>
                    <span className={totalShipping === 0 ? 'text-success-600 fw-bold text-sm' : 'text-gray-900 fw-bold text-sm'}>
                      {totalShipping === 0 ? 'FREE' : `Rs.${totalShipping.toFixed(0)}`}
                    </span>
                  </div>
                  <div className='mb-0 flex-between gap-8 pt-16 border-top border-gray-50'>
                    <span className='text-gray-900 fw-bold'>Total Amount</span>
                    <span className='text-main-600 text-2xl fw-bold'>
                      Rs.{cartTotal.toFixed(0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Checkout;